import React, { useState, useMemo } from 'react';
import {
  Box,
  ButtonBase,
  CardRow,
  Sticker,
  Typography,
  classes,
  styled,
  useTheme,
  useMediaQuery,
} from 'bonfire-kindling';
import { useTranslation } from 'react-i18next';

import { getLocations } from 'helpers/locations';
import { TimeRemaining, OpportunitySharedActions } from 'components';
import { getBonfireOpportunityPath } from '../../hooks/useIframeLogin';
import { useProjectClosingDate } from '../../hooks/useProjectClosingDate';
import DateGrid from './DateGrid';
import Flames from 'assets/BonfireFlames.png';

const StyledExpandButton = styled(ButtonBase)(({ theme }) => ({
  flex: 1,
  flexDirection: 'column',
  alignItems: 'stretch',
  textAlign: 'left',
  margin: theme.spacing(1),
  padding: theme.spacing(3),
  [`&.${classes.states.MuiFocusVisible}`]:
    theme.components.MuiButton.styleOverrides.root[`&.${classes.states.MuiFocusVisible}`],
}));

const StyledCardContainer = styled(Box)(({ theme, isFollowing }) => ({
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
  borderLeft: '7px solid',
  borderLeftColor: isFollowing ? theme.palette.primary.main : 'transparent',
  [theme.breakpoints.up('sm')]: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingRight: theme.spacing(4),
  },
}));
const StyledProjectContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: theme.spacing(2),
    '& > *:not(:last-child)': { marginRight: theme.spacing(3) },
  },
}));

export default function OpportunitiesCard({
  Locations,
  OpportunityIntentToBid,
  DateOpen,
  DateClose,
  DateIntentToBidDue,
  DateQuestionsDue,
  ProjectID,
  ProjectName,
  ReferenceID,
  StatementOfWork,
  Organization,
  ExternalLink,
  logInToOrganizationDomain,
  IsFollowing,
  toggleIsFollowing,
}) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const isMobileWidth = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const isWide = useMediaQuery(theme => theme.breakpoints.up('lg'));

  const [isFollowing, setIsFollowing] = useState(IsFollowing);
  const [isExpanded, setIsExpanded] = useState(false);

  const { isOpenProject, isClosingSoon, remaining } = useProjectClosingDate(DateClose);

  const { summary: locationsSummary, extended: extendedLocations } = useMemo(
    () => getLocations(Locations),
    [Locations]
  );
  const detailsId = `${ProjectID}ProjectDetails`;

  const orgInfo = (
    <Box display="flex" alignItems="center">
      <img
        alt={t('opportunities.project.logo')}
        src={Organization.OrganizationLogoURL}
        style={{ marginRight: '1rem', width: '50px' }}
      />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {Organization.OrganizationName}
        </Typography>
        <Typography variant="body2">{locationsSummary}</Typography>
      </Box>
    </Box>
  );
  const statusSticker = (
    <Sticker background={isOpenProject ? palette.green.dark : palette.grey.light}>
      {isOpenProject ? t('opportunities.project.open') : t('opportunities.project.closed')}
    </Sticker>
  );
  const timeRemaining = isOpenProject && (
    <Box sx={{ display: 'block', overflow: 'visible' }}>
      <TimeRemaining
        label={
          isClosingSoon
            ? t('opportunities.project.time_remaining')
            : t('opportunities.project.days_left', { count: remaining.days })
        }
        value={isClosingSoon ? remaining.hours : remaining.days}
      />
    </Box>
  );
  const bonfireExclusive = Organization.IsBonfireOrganization && (
    <Box sx={{ display: 'flex', alignItems: 'center' }} pb={isMobileWidth ? 2 : 0}>
      <img
        src={Flames}
        alt=""
        style={{
          height: '1.25rem',
          width: '2rem',
          paddingRight: '0.25rem',
        }}
      />
      <Typography component="span" variant="body2">
        {t('opportunities.project.bonfire_exclusive')}
      </Typography>
    </Box>
  );
  const dateProps = {
    OpportunityIntentToBid,
    DateOpen,
    DateClose,
    DateIntentToBidDue,
    DateQuestionsDue,
    OpportunityQAndA: Organization.OpportunityQAndA,
  };
  return (
    <Box m={2}>
      <CardRow>
        <StyledCardContainer isFollowing={isFollowing} data-cy={`${ProjectID}-opportunity-card`}>
          <StyledExpandButton
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={!!isExpanded}
            aria-controls={detailsId}
            disableTouchRipple
          >
            {!isMobileWidth && (
              <>
                <Typography variant="h4" gutterBottom>
                  {ProjectName}
                </Typography>
                {bonfireExclusive}
              </>
            )}
            <StyledProjectContentContainer>
              {isMobileWidth ? (
                <>
                  <Box display="flex">
                    <Box mr="auto">
                      <Typography variant="h4" gutterBottom>
                        {ProjectName}
                      </Typography>
                      {bonfireExclusive}
                      {statusSticker}
                    </Box>
                    {timeRemaining}
                  </Box>
                  {orgInfo}
                </>
              ) : (
                <>
                  {statusSticker}
                  <Box display="flex" alignItems="center" sx={{ width: { sm: '40%', lg: '25%' } }}>
                    {orgInfo}
                  </Box>
                  {timeRemaining}
                  {isWide && <DateGrid {...dateProps} />}
                </>
              )}
            </StyledProjectContentContainer>
            {!isWide && <DateGrid {...dateProps} />}
          </StyledExpandButton>
          <OpportunitySharedActions
            ExternalLink={ExternalLink}
            IsBonfireOrganization={Organization.IsBonfireOrganization}
            openBonfireOpportunity={e => {
              e.stopPropagation();
              logInToOrganizationDomain(
                `https://${Organization.Domain}`,
                getBonfireOpportunityPath(ProjectID)
              );
            }}
            isFollowing={isFollowing}
            toggleIsFollowing={e => {
              e.stopPropagation();
              setIsFollowing(v => !v);
              toggleIsFollowing();
            }}
          />
        </StyledCardContainer>
        <Box id={detailsId}>
          {isExpanded && (
            <Box px={4} pb={4}>
              <div style={{ display: 'flex' }}>
                <Typography variant="body2" style={{ fontWeight: 'bold', marginRight: '10px' }}>
                  {t('opportunities.project.reference_number')}
                </Typography>
                <Typography variant="body2">{ReferenceID}</Typography>
              </div>
              <Typography variant="body2">
                {StatementOfWork && StatementOfWork !== ''
                  ? StatementOfWork
                  : t('opportunities.project.default_description')}
              </Typography>
              {extendedLocations && (
                <div style={{ display: 'flex' }}>
                  <Typography variant="body2" style={{ fontWeight: 'bold', marginRight: '10px' }}>
                    {t('opportunities.project.locations')}
                  </Typography>
                  <Typography variant="body2">{extendedLocations}</Typography>
                </div>
              )}
            </Box>
          )}
        </Box>
      </CardRow>
    </Box>
  );
}
